import { useId, type MouseEvent, type ReactNode } from 'react';

/** Shared behavior for Button and IconButton. */
export const useButtonState = ({
  disabled,
  disabledReason,
  loading,
  onClick,
  describedBy,
}: {
  disabled?: boolean;
  disabledReason?: ReactNode;
  loading?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  describedBy?: string;
}) => {
  const reasonId = useId();
  const hasReason =
    Boolean(disabled) &&
    disabledReason !== undefined &&
    disabledReason !== null;
  // Native `disabled` removes the button from the tab order and swallows
  // pointer events, which would hide the tooltip and the reason. Buttons
  // with a reason (or a pending action) use aria-disabled instead.
  const softDisabled = Boolean(loading) || hasReason;
  const ariaDescribedBy =
    [describedBy, hasReason ? reasonId : undefined].filter(Boolean).join(' ') ||
    undefined;

  return {
    hasReason,
    reasonId,
    styleState: { disabled: Boolean(disabled), loading: Boolean(loading) },
    buttonProps: {
      disabled: Boolean(disabled) && !softDisabled,
      'aria-disabled': softDisabled || undefined,
      'aria-busy': loading || undefined,
      'data-disabled': disabled || loading ? '' : undefined,
      ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
      onClick: (event: MouseEvent<HTMLButtonElement>) => {
        if (softDisabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      },
    },
  };
};
